import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import {
  FaMapMarkerAlt, FaClock, FaBriefcase, FaCalendar,
  FaArrowRight, FaArrowLeft, FaStar, FaCheck,
  FaEnvelope, FaLinkedin, FaGlobe,
  FaPhone, FaUser, FaFileAlt, FaSpinner,
} from 'react-icons/fa';
import PageTransition from '../components/PageTransition';
import { getCareer, incrementCareerViews, submitContact } from '../utils/api';
import {
  fadeInUp, staggerContainer, viewport,
} from '../utils/animations';

const typeStyles = {
  'full-time':  'bg-emerald-900/30 border-emerald-700/40 text-emerald-400',
  'part-time':  'bg-amber-900/30 border-amber-700/40 text-amber-400',
  'contract':   'bg-blue-900/30 border-blue-700/40 text-blue-400',
  'internship': 'bg-purple-900/30 border-purple-700/40 text-purple-400',
};

const getTypeLabel = (type) =>
  type ? type.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '';

const formatDate = (date) =>
  new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const DEMO_JOBS = [
  {
    _id: '1',
    slug: 'full-stack-developer',
    title: 'Full Stack Developer',
    department: 'Engineering',
    location: 'Remote',
    type: 'full-time',
    experience: '3-5 Years',
    description: 'We are looking for a talented Full Stack Developer to join our Engineering team and help build cutting-edge web applications for our clients. You will work across the entire stack, from crafting beautiful, responsive UIs to designing robust APIs and scalable backend systems.',
    responsibilities: [
      'Design, build, and maintain high-performance full-stack web applications.',
      'Collaborate with product managers and designers to deliver exceptional user experiences.',
      'Write clean, well-tested, and well-documented code across the front-end and back-end.',
      'Architect scalable RESTful APIs and integrate third-party services.',
      'Participate in code reviews and contribute to engineering best practices.',
      'Troubleshoot and resolve complex technical issues in production.',
      'Mentor junior engineers and contribute to a culture of continuous improvement.',
    ],
    requirements: [
      '3–5 years of professional experience in full-stack web development.',
      'Strong proficiency in React.js and Node.js (or similar modern stack).',
      'Solid understanding of databases — both SQL (PostgreSQL/MySQL) and NoSQL (MongoDB).',
      'Experience with RESTful API design, authentication (JWT/OAuth), and cloud platforms (AWS/GCP/Azure).',
      'Familiarity with CI/CD pipelines, Docker, and version control (Git).',
      'Strong problem-solving skills and attention to detail.',
      'Excellent written and verbal communication skills.',
    ],
    niceToHave: [
      'Experience with TypeScript and GraphQL.',
      'Familiarity with microservices architecture.',
      'Contributions to open-source projects.',
      'Experience with real-time systems (WebSockets, Socket.io).',
      'Knowledge of performance optimisation and web security best practices.',
    ],
    benefits: [
      'Competitive salary and performance bonuses.',
      'Fully remote — work from anywhere in the world.',
      'Flexible working hours.',
      'Annual learning & development budget ($1,500/year).',
      'Comprehensive health and wellness coverage.',
      'Regular team off-sites and virtual events.',
    ],
    active: true,
    postedDate: new Date().toISOString(),
  },
  {
    _id: '2',
    slug: 'sales-representative',
    title: 'Sales Representative',
    department: 'Sales',
    location: 'Remote',
    type: 'full-time',
    experience: '2-4 Years',
    description: 'We are seeking a driven Sales Representative to join our growing Sales team. You will identify and close new business opportunities, manage client relationships, and help shape our go-to-market strategy.',
    responsibilities: [
      'Identify, prospect, and qualify new business opportunities through outbound and inbound channels.',
      'Manage the full sales cycle from lead generation to deal closure.',
      'Build and maintain strong, long-term relationships with clients.',
      'Collaborate with marketing to develop targeted outreach campaigns.',
      'Maintain accurate records of all sales activities in the CRM.',
      'Meet and exceed monthly and quarterly revenue targets.',
      'Provide feedback from the market to inform product and strategy decisions.',
    ],
    requirements: [
      '2–4 years of B2B sales experience, preferably in tech or digital services.',
      'Proven track record of consistently hitting or exceeding sales quotas.',
      'Strong communication, negotiation, and presentation skills.',
      'Experience with CRM tools (HubSpot, Salesforce, or similar).',
      'Self-motivated with a strong sense of ownership and accountability.',
      'Ability to quickly understand technical products and communicate value clearly.',
    ],
    niceToHave: [
      'Experience selling software development or digital transformation services.',
      'Existing network of decision-makers in relevant industries.',
      'Familiarity with sales engagement tools (Apollo, Outreach, LinkedIn Sales Navigator).',
      'Experience in a startup or fast-growth environment.',
    ],
    benefits: [
      'Competitive base salary plus uncapped commission structure.',
      'Fully remote with flexible working hours.',
      'Annual sales conference and team retreats.',
      'Learning & development budget for sales enablement tools and courses.',
      'Health and wellness benefits.',
      'Clear career progression path to Senior and Lead roles.',
    ],
    active: true,
    postedDate: new Date().toISOString(),
  },
  {
    _id: '3',
    slug: 'ui-ux-designer',
    title: 'UI/UX Designer',
    department: 'Design',
    location: 'Remote',
    type: 'full-time',
    experience: '2-4 Years',
    description: 'We are looking for a creative and user-obsessed UI/UX Designer to join our Design team. You will own the end-to-end design process, from user research and wireframing to high-fidelity prototypes and final handoff. Working closely with engineers and product managers, you will turn complex problems into intuitive, beautiful digital experiences.',
    responsibilities: [
      'Lead end-to-end design for web and mobile products — from research and ideation to final handoff.',
      'Create wireframes, user flows, prototypes, and high-fidelity mockups.',
      'Conduct user research, usability testing, and translate insights into design decisions.',
      'Collaborate with engineers to ensure pixel-perfect, accessible implementation.',
      'Define and maintain a consistent design system and component library.',
      'Work closely with product managers to prioritise and scope design work.',
      'Champion user-centred design principles across the organisation.',
    ],
    requirements: [
      '2–4 years of professional UI/UX design experience for digital products.',
      'Strong portfolio demonstrating user-centred design process and high-quality visual output.',
      'Proficiency in Figma (prototyping, auto-layout, components, dev mode).',
      'Solid understanding of usability, accessibility (WCAG), and responsive design.',
      'Experience conducting user interviews, surveys, and usability tests.',
      'Ability to communicate design decisions clearly to both technical and non-technical stakeholders.',
    ],
    niceToHave: [
      'Motion design skills (Lottie, After Effects, or Framer).',
      'Experience with design tokens and advanced design system architecture.',
      'Basic understanding of HTML/CSS for smoother developer collaboration.',
      'Experience designing for SaaS or enterprise software.',
      'Familiarity with analytics tools (Mixpanel, Hotjar) to inform design.',
    ],
    benefits: [
      'Competitive salary commensurate with experience.',
      'Fully remote with flexible working hours.',
      'Annual budget for design tools, courses, and conferences.',
      'Access to premium design resources and software.',
      'Health and wellness coverage.',
      'Collaborative, feedback-driven culture with fast career growth.',
    ],
    active: true,
    postedDate: new Date().toISOString(),
  },
];

const EMPTY_FORM = {
  firstName: '', lastName: '', email: '', phone: '',
  currentLocation: '', resumeUrl: '', portfolioUrl: '',
  linkedin: '', yearsOfExperience: '',
  currentCompany: '', noticePeriod: '', expectedSalary: '', coverLetter: '',
};

const CareerDetail = () => {
  const { slug } = useParams();
  const applyRef = useRef(null);

  const demoJob = DEMO_JOBS.find(j => j.slug === slug) || null;

  const [job, setJob]           = useState(demoJob);
  const loading = false;
  const [notFound, setNotFound] = useState(!demoJob);

  const [form, setForm]               = useState(EMPTY_FORM);
  const [submitting, setSubmitting]   = useState(false);
  const [submitted, setSubmitted]     = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    fetchJob();
  }, [slug]); // eslint-disable-line

  const fetchJob = async () => {
    try {
      const result = await getCareer(slug);
      const career = result?.data || result;
      if (career?._id) {
        setJob(career);
        setNotFound(false);
        incrementCareerViews(slug).catch(() => {});
      }
    } catch {
      // keep demo data already in state
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');

    const message = [
      `Position Applied For: ${job?.title}`,
      `Department: ${job?.department}`,
      '',
      `Current Location: ${form.currentLocation || 'N/A'}`,
      `Resume/CV URL: ${form.resumeUrl}`,
      `Portfolio URL: ${form.portfolioUrl || 'N/A'}`,
      `LinkedIn: ${form.linkedin || 'N/A'}`,
      `Years of Experience: ${form.yearsOfExperience || 'N/A'}`,
      `Current Company: ${form.currentCompany || 'N/A'}`,
      `Notice Period: ${form.noticePeriod || 'N/A'}`,
      `Expected Salary: ${form.expectedSalary || 'N/A'}`,
      '',
      `Cover Letter:\n${form.coverLetter || 'N/A'}`,
    ].join('\n');

    try {
      await submitContact({
        name: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email,
        phone: form.phone,
        company: form.currentCompany,
        subject: `Job Application: ${job?.title}`,
        message,
      });
      setSubmitted(true);
      setForm(EMPTY_FORM);
    } catch {
      setSubmitError('Something went wrong. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToApply = () => {
    applyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /* Loading */
  if (loading) {
    return (
      <PageTransition>
        <div className="bg-transparent min-h-screen flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 rounded-full border-2 border-gray-700 border-t-white animate-spin" />
            <p className="text-gray-500 text-sm tracking-wide">Loading job details…</p>
          </div>
        </div>
      </PageTransition>
    );
  }

  /* Not Found */
  if (notFound || !job) {
    return (
      <PageTransition>
        <div className="bg-transparent min-h-screen flex items-center justify-center">
          <motion.div
            variants={staggerContainer} initial="hidden" animate="visible"
            className="text-center max-w-md mx-auto px-6"
          >
            <motion.div variants={fadeInUp} className="text-6xl mb-6">🔍</motion.div>
            <motion.h1 variants={fadeInUp} className="text-3xl font-black text-white mb-4">Position Not Found</motion.h1>
            <motion.p variants={fadeInUp} className="text-gray-400 mb-8">
              This role may have been filled or removed. Check our other open positions.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link
                to="/careers"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gray-950 font-bold rounded-xl hover:bg-gray-100 transition-all duration-300"
              >
                <FaArrowLeft className="text-sm" />
                Back to Careers
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="bg-transparent min-h-screen">

        {/* Hero Header */}
        <section className="relative pt-32 pb-14 overflow-hidden">
          <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[400px] rounded-full bg-gray-700/10 blur-3xl pointer-events-none" />

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-6xl">

            {/* Back link */}
            <motion.div variants={fadeInUp} initial="hidden" animate="visible" className="mb-8">
              <Link
                to="/careers"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm font-semibold transition-colors duration-200 group"
              >
                <FaArrowLeft className="text-xs group-hover:-translate-x-0.5 transition-transform duration-200" />
                Back to Careers
              </Link>
            </motion.div>

            <motion.div
              variants={staggerContainer} initial="hidden" animate="visible"
            >
              {/* Badges row */}
              <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-3 mb-6">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-900/60 border border-gray-700 rounded-full">
                  <FaStar className="text-gray-400 text-[10px]" />
                  <span className="text-[11px] font-semibold tracking-widest text-gray-300 uppercase">Featured Position</span>
                </div>
                <span className="px-3 py-1.5 rounded-full bg-gray-800/80 border border-gray-700 text-gray-300 text-[11px] font-semibold uppercase tracking-widest">
                  {job.department}
                </span>
                <span className={`px-3 py-1.5 rounded-full text-[11px] font-bold border tracking-wide ${typeStyles[job.type] || 'bg-gray-800 border-gray-700 text-gray-300'}`}>
                  {getTypeLabel(job.type)}
                </span>
              </motion.div>

              {/* Title */}
              <motion.h1
                variants={fadeInUp}
                className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-8 leading-tight"
              >
                {job.title}
              </motion.h1>

              {/* Meta chips */}
              <motion.div variants={fadeInUp} className="flex flex-wrap gap-5">
                {job.location && (
                  <div className="flex items-center gap-2 text-gray-400 text-sm">
                    <FaMapMarkerAlt className="text-gray-500 text-xs" />
                    <span>{job.location}</span>
                  </div>
                )}
                {job.experience && (
                  <div className="flex items-center gap-2 text-gray-400 text-sm">
                    <FaBriefcase className="text-gray-500 text-xs" />
                    <span>{job.experience}</span>
                  </div>
                )}
                {job.postedDate && (
                  <div className="flex items-center gap-2 text-gray-400 text-sm">
                    <FaCalendar className="text-gray-500 text-xs" />
                    <span>Posted {formatDate(job.postedDate)}</span>
                  </div>
                )}
                {job.salary && (
                  <div className="flex items-center gap-2 text-gray-400 text-sm">
                    <FaClock className="text-gray-500 text-xs" />
                    <span>{job.salary}</span>
                  </div>
                )}
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <section className="relative pb-24 overflow-hidden">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <div className="flex flex-col lg:flex-row gap-10">

              {/* Left Column */}
              <div className="flex-1 space-y-8">

                {/* About the Role */}
                <motion.div
                  variants={fadeInUp} initial="hidden" whileInView="visible" viewport={viewport}
                  className="glass-card rounded-2xl p-8"
                >
                  <h2 className="text-xl font-black text-white mb-5 flex items-center gap-3">
                    <span className="w-1 h-6 bg-white rounded-full inline-block" />
                    About the Role
                  </h2>
                  <p className="text-gray-300 leading-relaxed whitespace-pre-line">{job.description}</p>
                </motion.div>

                {/* Responsibilities */}
                {job.responsibilities?.length > 0 && (
                  <motion.div
                    variants={fadeInUp} initial="hidden" whileInView="visible" viewport={viewport}
                    className="glass-card rounded-2xl p-8"
                  >
                    <h2 className="text-xl font-black text-white mb-6 flex items-center gap-3">
                      <span className="w-1 h-6 bg-white rounded-full inline-block" />
                      Responsibilities
                    </h2>
                    <ul className="space-y-3">
                      {job.responsibilities.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-gray-300 text-sm leading-relaxed">
                          <FaCheck className="text-gray-400 text-xs mt-1 shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )}

                {/* Requirements */}
                {job.requirements?.length > 0 && (
                  <motion.div
                    variants={fadeInUp} initial="hidden" whileInView="visible" viewport={viewport}
                    className="glass-card rounded-2xl p-8"
                  >
                    <h2 className="text-xl font-black text-white mb-6 flex items-center gap-3">
                      <span className="w-1 h-6 bg-white rounded-full inline-block" />
                      Requirements
                    </h2>
                    <ul className="space-y-3">
                      {job.requirements.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-gray-300 text-sm leading-relaxed">
                          <FaCheck className="text-gray-400 text-xs mt-1 shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )}

                {/* Nice to Have */}
                {job.niceToHave?.length > 0 && (
                  <motion.div
                    variants={fadeInUp} initial="hidden" whileInView="visible" viewport={viewport}
                    className="glass-card rounded-2xl p-8"
                  >
                    <h2 className="text-xl font-black text-white mb-6 flex items-center gap-3">
                      <span className="w-1 h-6 bg-white rounded-full inline-block" />
                      Nice to Have
                    </h2>
                    <ul className="space-y-3">
                      {job.niceToHave.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-gray-300 text-sm leading-relaxed">
                          <FaStar className="text-gray-500 text-xs mt-1 shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )}

                {/* Benefits */}
                {job.benefits?.length > 0 && (
                  <motion.div
                    variants={fadeInUp} initial="hidden" whileInView="visible" viewport={viewport}
                    className="glass-card rounded-2xl p-8"
                  >
                    <h2 className="text-xl font-black text-white mb-6 flex items-center gap-3">
                      <span className="w-1 h-6 bg-white rounded-full inline-block" />
                      Benefits
                    </h2>
                    <ul className="space-y-3">
                      {job.benefits.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-gray-300 text-sm leading-relaxed">
                          <FaCheck className="text-gray-400 text-xs mt-1 shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )}
              </div>

              {/* Right Sidebar */}
              <div className="lg:w-80 shrink-0">
                <div className="lg:sticky lg:top-24 space-y-5">

                  {/* Job Details Card */}
                  <motion.div
                    variants={fadeInUp} initial="hidden" whileInView="visible" viewport={viewport}
                    className="glass-card rounded-2xl p-7"
                  >
                    <h3 className="text-white font-black text-lg mb-6">Job Details</h3>
                    <div className="space-y-5">
                      {job.experience && (
                        <div className="flex items-start gap-4">
                          <div className="w-9 h-9 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center shrink-0">
                            <FaBriefcase className="text-gray-400 text-sm" />
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold tracking-widest text-gray-500 uppercase mb-0.5">Experience Level</p>
                            <p className="text-white font-semibold text-sm">{job.experience}</p>
                          </div>
                        </div>
                      )}
                      {job.type && (
                        <div className="flex items-start gap-4">
                          <div className="w-9 h-9 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center shrink-0">
                            <FaClock className="text-gray-400 text-sm" />
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold tracking-widest text-gray-500 uppercase mb-0.5">Job Type</p>
                            <p className="text-white font-semibold text-sm">{getTypeLabel(job.type)}</p>
                          </div>
                        </div>
                      )}
                      {job.location && (
                        <div className="flex items-start gap-4">
                          <div className="w-9 h-9 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center shrink-0">
                            <FaMapMarkerAlt className="text-gray-400 text-sm" />
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold tracking-widest text-gray-500 uppercase mb-0.5">Location</p>
                            <p className="text-white font-semibold text-sm">{job.location}</p>
                          </div>
                        </div>
                      )}
                      {job.salary && (
                        <div className="flex items-start gap-4">
                          <div className="w-9 h-9 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center shrink-0">
                            <span className="text-gray-400 text-sm font-bold">$</span>
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold tracking-widest text-gray-500 uppercase mb-0.5">Compensation</p>
                            <p className="text-white font-semibold text-sm">{job.salary}</p>
                          </div>
                        </div>
                      )}
                      {job.postedDate && (
                        <div className="flex items-start gap-4">
                          <div className="w-9 h-9 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center shrink-0">
                            <FaCalendar className="text-gray-400 text-sm" />
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold tracking-widest text-gray-500 uppercase mb-0.5">Posted</p>
                            <p className="text-white font-semibold text-sm">{formatDate(job.postedDate)}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mt-7 pt-6 border-t border-gray-800">
                      <button
                        onClick={scrollToApply}
                        className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-white text-gray-950 font-bold rounded-xl hover:bg-gray-100 transition-all duration-300 hover:-translate-y-0.5 shine-hover text-sm"
                      >
                        Apply for this Position
                        <FaArrowRight className="text-xs" />
                      </button>
                    </div>
                  </motion.div>

                  {/* Share / browse more */}
                  <motion.div
                    variants={fadeInUp} initial="hidden" whileInView="visible" viewport={viewport}
                    className="glass-card rounded-2xl p-6"
                  >
                    <p className="text-gray-500 text-xs text-center mb-4 tracking-wide">Looking for something else?</p>
                    <Link
                      to="/careers"
                      className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-gray-700 text-gray-300 text-sm font-semibold hover:border-gray-500 hover:text-white transition-all duration-300"
                    >
                      <FaArrowLeft className="text-xs" />
                      Browse All Openings
                    </Link>
                  </motion.div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* Application Form */}
        <section ref={applyRef} className="relative py-24 overflow-hidden border-t border-gray-800/60">
          <div className="absolute inset-0 dot-pattern opacity-20 pointer-events-none" />
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-3xl">

            <motion.div
              variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewport}
              className="text-center mb-14"
            >
              <motion.div variants={fadeInUp} className="inline-flex mb-5">
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-900/60 border border-gray-700 rounded-full">
                  <FaFileAlt className="text-gray-400 text-xs" />
                  <span className="text-xs font-semibold tracking-widest text-gray-300 uppercase">Apply Now</span>
                </div>
              </motion.div>
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-black text-white mb-4">
                Apply for <span className="animated-gradient-text">{job.title}</span>
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-gray-400 text-sm max-w-md mx-auto">
                Fill out the form below to submit your application. We'll review it and get back to you soon.
              </motion.p>
            </motion.div>

            {/* Success State */}
            {submitted ? (
              <motion.div
                variants={fadeInUp} initial="hidden" animate="visible"
                className="glass-card rounded-2xl p-12 text-center"
              >
                <div className="w-16 h-16 rounded-full bg-emerald-900/30 border border-emerald-700/40 flex items-center justify-center mx-auto mb-6">
                  <FaCheck className="text-emerald-400 text-2xl" />
                </div>
                <h3 className="text-2xl font-black text-white mb-3">Application Submitted!</h3>
                <p className="text-gray-400 mb-8 max-w-sm mx-auto">
                  Thank you for applying. We'll review your application and reach out if there's a good fit.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <button
                    onClick={() => setSubmitted(false)}
                    className="px-6 py-2.5 rounded-xl border border-gray-700 text-gray-300 text-sm font-semibold hover:border-gray-500 hover:text-white transition-all duration-300"
                  >
                    Apply Again
                  </button>
                  <Link
                    to="/careers"
                    className="px-6 py-2.5 rounded-xl bg-white text-gray-950 text-sm font-bold hover:bg-gray-100 transition-all duration-300"
                  >
                    Browse Other Roles
                  </Link>
                </div>
              </motion.div>
            ) : (
              <motion.form
                variants={fadeInUp} initial="hidden" whileInView="visible" viewport={viewport}
                onSubmit={handleSubmit}
                className="glass-card rounded-2xl p-8 md:p-10 space-y-6"
                noValidate
              >
                {/* Name row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="First Name" required>
                    <Input
                      name="firstName" value={form.firstName} onChange={handleChange}
                      placeholder="John" required icon={<FaUser />}
                    />
                  </Field>
                  <Field label="Last Name" required>
                    <Input
                      name="lastName" value={form.lastName} onChange={handleChange}
                      placeholder="Doe" required icon={<FaUser />}
                    />
                  </Field>
                </div>

                {/* Email + Phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Email" required>
                    <Input
                      name="email" type="email" value={form.email} onChange={handleChange}
                      placeholder="john@example.com" required icon={<FaEnvelope />}
                    />
                  </Field>
                  <Field label="Phone Number">
                    <Input
                      name="phone" type="tel" value={form.phone} onChange={handleChange}
                      placeholder="+1 234 567 8900" icon={<FaPhone />}
                    />
                  </Field>
                </div>

                {/* Location */}
                <Field label="Current Location">
                  <Input
                    name="currentLocation" value={form.currentLocation} onChange={handleChange}
                    placeholder="City, Country" icon={<FaMapMarkerAlt />}
                  />
                </Field>

                {/* Resume URL */}
                <Field label="Resume / CV URL" required hint="Link to Google Drive, Dropbox, etc.">
                  <Input
                    name="resumeUrl" type="url" value={form.resumeUrl} onChange={handleChange}
                    placeholder="https://drive.google.com/..." required icon={<FaFileAlt />}
                  />
                </Field>

                {/* Portfolio + LinkedIn + Years of Experience */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Portfolio URL">
                    <Input
                      name="portfolioUrl" type="url" value={form.portfolioUrl} onChange={handleChange}
                      placeholder="https://yourportfolio.com" icon={<FaGlobe />}
                    />
                  </Field>
                  <Field label="LinkedIn Profile">
                    <Input
                      name="linkedin" type="url" value={form.linkedin} onChange={handleChange}
                      placeholder="https://linkedin.com/in/..." icon={<FaLinkedin />}
                    />
                  </Field>
                </div>

                {/* Years of Experience */}
                <Field label="Years of Experience">
                  <Input
                    name="yearsOfExperience" value={form.yearsOfExperience} onChange={handleChange}
                    placeholder="e.g. 4 years" icon={<FaBriefcase />}
                  />
                </Field>

                {/* Current Company + Notice Period */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Current Company">
                    <Input
                      name="currentCompany" value={form.currentCompany} onChange={handleChange}
                      placeholder="Acme Corp" icon={<FaBriefcase />}
                    />
                  </Field>
                  <Field label="Notice Period">
                    <Input
                      name="noticePeriod" value={form.noticePeriod} onChange={handleChange}
                      placeholder="e.g. 2 weeks" icon={<FaClock />}
                    />
                  </Field>
                </div>

                {/* Expected Salary */}
                <Field label="Expected Salary">
                  <Input
                    name="expectedSalary" value={form.expectedSalary} onChange={handleChange}
                    placeholder="e.g. $80,000/year" icon={<span className="font-bold text-xs">$</span>}
                  />
                </Field>

                {/* Cover Letter */}
                <Field label="Cover Letter">
                  <textarea
                    name="coverLetter"
                    value={form.coverLetter}
                    onChange={handleChange}
                    rows={5}
                    placeholder="Tell us why you're a great fit for this role…"
                    className="w-full px-4 py-3 rounded-xl bg-gray-900/60 border border-gray-700 text-white placeholder-gray-600 text-sm focus:outline-none focus:border-gray-500 focus:ring-1 focus:ring-gray-500 resize-none transition-colors duration-200"
                  />
                </Field>

                {/* Error */}
                {submitError && (
                  <p className="text-red-400 text-sm text-center">{submitError}</p>
                )}

                {/* Submit */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-white text-gray-950 font-bold rounded-xl hover:bg-gray-100 transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none shine-hover text-sm"
                  >
                    {submitting ? (
                      <>
                        <FaSpinner className="animate-spin text-sm" />
                        Submitting…
                      </>
                    ) : (
                      <>
                        Submit Application
                        <FaArrowRight className="text-xs" />
                      </>
                    )}
                  </button>
                </div>
              </motion.form>
            )}
          </div>
        </section>

      </div>
    </PageTransition>
  );
};

/* Helper sub-components */

const Field = ({ label, required, hint, children }) => (
  <div>
    <label className="block text-xs font-semibold tracking-wide text-gray-400 uppercase mb-2">
      {label}{required && <span className="text-gray-400 ml-1">*</span>}
    </label>
    {children}
    {hint && <p className="text-gray-600 text-xs mt-1.5">{hint}</p>}
  </div>
);

const Input = ({ icon, ...props }) => (
  <div className="relative">
    {icon && (
      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-600 text-xs pointer-events-none">
        {icon}
      </span>
    )}
    <input
      {...props}
      className={`w-full py-3 rounded-xl bg-gray-900/60 border border-gray-700 text-white placeholder-gray-600 text-sm focus:outline-none focus:border-gray-500 focus:ring-1 focus:ring-gray-500 transition-colors duration-200 ${icon ? 'pl-9 pr-4' : 'px-4'}`}
    />
  </div>
);

export default CareerDetail;
